import * as React from 'react';
import { useSelector } from 'react-redux';

import { selectUserById } from '../../../store/selectors/users';
import { getFullName } from '../../../utils/helpers';
import { capitalize } from '../../../utils/ui';
import { ActionButton, BackButton } from '../../forms';
import Loader from '../../Loader/Loader';
import ModalActions from '../ModalActions/ModalActions';
import ModalHeader from '../ModalHeader/ModalHeader';

import styles from './CommonModals.module.scss';

interface IProps {
  onClose: () => void;
  onDelete: () => void;
  assetName?: string;
  userId?: string;
}

const DeleteItemModal: React.FC<IProps> = ({ onClose, onDelete, assetName = 'item', userId }: IProps) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const user = useSelector(userId ? selectUserById(userId) : () => undefined);
  const userName = getFullName(user?.first_name, user?.last_name);

  const name = React.useMemo(() => assetName.toLowerCase(), [assetName]);
  const capitalizedName = React.useMemo(() => capitalize(name, true), [name]);

  const handleDeleteItem = React.useCallback(() => {
    onDelete();
    setIsLoading(true);
  }, [onDelete]);

  const DELETE_USER_TEXT = React.useMemo(() => {
    return (
      <>
        This action can't be undone. After you click <span className={styles.boldText}>Delete</span>, we'll check if{' '}
        <span className={styles.boldText}>{userName}</span> has open orders. If they do, you'll need to transfer those
        orders to another user before deleting.
      </>
    );
  }, [userName]);

  return (
    <div className={styles.container}>
      <Loader isLoading={isLoading} className={styles.loader} />
      <ModalHeader className={styles.header} left={<BackButton className={styles.back} onClick={onClose} />} />
      <div className={styles.content}>
        <div className={styles.contentHeader}>Delete {userId ? userName : capitalizedName}</div>
        <div className={styles.description}>
          {userId ? DELETE_USER_TEXT : `Are you sure you want to delete this ${name}?`}
        </div>
      </div>
      <ModalActions className={styles.footer}>
        <ActionButton
          outlined
          className={styles.actionBtn}
          title="Cancel"
          onClick={onClose}
          dataAction="cancel_delete_btn"
        />
        <ActionButton
          className={styles.actionBtn}
          title="Delete"
          onClick={handleDeleteItem}
          dataAction="submit_delete_btn"
        />
      </ModalActions>
    </div>
  );
};

export default DeleteItemModal;
